import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  PlanDTO,
  PlanDTOFromJSON,
  SloGroupDTO,
  SloGroupDTOFromJSON,
  SloType,
} from '@atlassiansox/phobos-client-ts';
import { BooleanParam, useQueryParam } from 'use-query-params';
import SectionMessage from '@atlaskit/section-message';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { Link, useParams } from 'react-router-dom';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { useQuery } from '@tanstack/react-query';
import BitbucketPipelinesIcon from '@atlaskit/icon/glyph/bitbucket/pipelines';

import RevisionLogs from '@/components/primitive/revision-logs';
import { apiFetch } from '@/services';
import { FetchError } from '@/services/api-service/api-service';
import { useFetchErrorController, useNavigationController } from '@/controllers';
import SloPageHeader from '@/components/composed/new-slo-views/expanded/header';
import {
  CapabilityEntityLink,
  KeyValue,
  Item,
  EntityPageSkeleton,
  BreachCount,
} from '@/components/primitive';
import {
  breakWord,
  displayNone,
  flexStart,
  spaceGapMinimum,
  spaceTopComfortable,
  spaceTopCompact,
  spaceTopMinimum,
  spaceTopRelaxed,
} from '@/styles';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { stringReplaceLink } from '@/utils';
import Summary from '@/components/composed/slo-group/summary';
import {
  enlargedIconStyles,
  tabWithLinkStyles,
  tabWithLinkTitleStyles,
} from '@/components/composed/page-content-tabs/styles';
import { PageContentTabNames } from '@/components/composed/page-content-tabs/constants';
import TerraformIcon from '@/components/primitive/terraform-icon';
import SLOPanel from '@/components/composed/slo-group/slo-panel';
import { StageDisplayContext, StageDisplayType } from '@/controllers/stage-display-controller';
import { querySpec } from '@/services/api-service/api-queries';
import SyncPanel from '@/components/composed/slo-group/sync-panel';
import ConfigurationPanel from '@/components/composed/slo-group/configuration-panel';

import * as styles from '../slo-view/styles';

import { iconGroupStyles } from './styles';

export type SLOGroupViewProps = {
  tabName?: PageContentTabNames;
};

const SLOGroupView: React.FC<SLOGroupViewProps> = ({ tabName }) => {
  const { onError } = useFetchErrorController();
  const { onNavigation } = useNavigationController();

  const { publicId: publicIdParam } = useParams<{
    publicId: string;
  }>();
  const publicId = publicIdParam ?? '';

  const { queryKey, path } = querySpec('slo-group.{id}', { id: publicId });
  const { data: dataRaw, isInitialLoading, error } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const data: SloGroupDTO = useMemo(() => SloGroupDTOFromJSON(dataRaw || {}), [dataRaw]);

  const [runbookParam, setRunbookParam] = useQueryParam('runbook', BooleanParam);
  const [showRunbookWarning, setShowRunbookWarning] = useState(false);

  const { stageDisplayType } = useContext(StageDisplayContext);
  const isFullScreen = stageDisplayType === StageDisplayType.FullScreen;

  useEffect(() => {
    if (!isInitialLoading && error instanceof FetchError && error.response.status === 404) {
      onNavigation('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isInitialLoading]);

  useEffect(() => {
    if (runbookParam) {
      if (data?.runbookUrl) {
        window.location.href = data.runbookUrl;
      } else {
        setShowRunbookWarning(true);
        setRunbookParam(undefined);
      }
    }
  }, [data, runbookParam, setRunbookParam, setShowRunbookWarning]);

  // Lazy load the sync tab content only once
  const [syncInProgress, setSyncInProgress] = useState(false);
  const { queryKey: queryKeySync, path: pathSync } = querySpec('slo-group.sync.{id}', {
    id: publicId,
  });
  const [enableSync, setEnableSync] = useState(tabName === PageContentTabNames.SYNC);
  const { data: dataSyncRaw } = useQuery(queryKeySync, () => apiFetch(pathSync), {
    onError: (err: FetchError) => {
      if (err instanceof FetchError && err?.response?.status === 409) {
        setSyncInProgress(true);
        return;
      }
      onError(err);
    },
    enabled: enableSync,
  });
  const dataSync: PlanDTO = useMemo(() => PlanDTOFromJSON(dataSyncRaw || {}), [dataSyncRaw]);
  const hasSyncPlan = (dataSync?.operations ?? []).length > 0;
  useEffect(() => {
    // setTimeout is not necessarily needed as it's already non-block in
    // useEffect, but let's just be very specific to lazy load the sync
    setTimeout(() => {
      if (!enableSync) setEnableSync(true);
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let selectedIndex: number;
  switch (tabName) {
    case PageContentTabNames.CONFIGURATION:
      selectedIndex = 1;
      break;
    case PageContentTabNames.HISTORY:
      selectedIndex = 2;
      break;
    case PageContentTabNames.SYNC:
      selectedIndex = 3;
      break;
    default:
      selectedIndex = 0;
  }

  if (!dataRaw || isInitialLoading)
    return (
      <div css={spaceTopComfortable}>
        <EntityPageSkeleton />
      </div>
    );

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title={data.name} scope="SLO Group">
        {!isFullScreen && (
          <>
            <div css={styles.parentBlock}>
              <KeyValue title="Parent Capability">
                <CapabilityEntityLink
                  name={data.parent.name}
                  publicId={data.parent.publicId}
                  shouldTruncate={false}
                />
              </KeyValue>
            </div>
            <div css={spaceTopComfortable}>
              <SloPageHeader
                publicId={data.publicId}
                name={data.name}
                type={data.type as SloType}
                isDraft={false}
                parentCapabilityPublicId={data.parent.publicId}
              />
            </div>
            <div css={[spaceTopMinimum, breakWord]}>
              <h3 css={[flexStart, spaceGapMinimum]}>
                <span css={iconGroupStyles}>
                  <ChildIssuesIcon label="SLO Group Icon" size="small" />
                </span>
                {data.name}
              </h3>
            </div>
            {showRunbookWarning && (
              <div css={[spaceTopRelaxed]}>
                <SectionMessage appearance="warning">
                  The Runbook URL for this SLO group has not been configured. Please configure it in
                  Terraform resource.
                </SectionMessage>
              </div>
            )}
            {data.description && (
              <div css={[spaceTopRelaxed, styles.descriptionBlock]}>
                <Item title="Description">
                  <div css={spaceTopCompact}>{stringReplaceLink(data.description)}</div>
                </Item>
              </div>
            )}
            <Summary sloGroupSummary={data.sloGroupSummary} />
          </>
        )}

        <div css={[isFullScreen ? '' : spaceTopRelaxed]}>
          <Tabs selected={selectedIndex} id="page-content-tabs">
            <span css={isFullScreen ? displayNone : ''}>
              <TabList>
                <Tab>
                  <span css={tabWithLinkStyles}>
                    <Link relative="path" to={`/slo-group/${publicId}`}>
                      <span css={tabWithLinkTitleStyles}>
                        {selectedIndex === 0 ? (
                          <MoreIcon label="" size="small" />
                        ) : (
                          <ChildIssuesIcon label="" size="small" />
                        )}
                        SLOs
                      </span>
                    </Link>
                  </span>
                </Tab>
                <Tab>
                  <span css={tabWithLinkStyles}>
                    <Link relative="path" to={`/slo-group/${publicId}/configuration`}>
                      <span css={tabWithLinkTitleStyles}>
                        {selectedIndex === 1 ? (
                          <MoreIcon label="" size="small" />
                        ) : (
                          <TerraformIcon />
                        )}
                        Configuration
                      </span>
                    </Link>
                  </span>
                </Tab>
                <Tab>
                  <span css={tabWithLinkStyles}>
                    <Link relative="path" to={`/slo-group/${publicId}/history`}>
                      <span css={tabWithLinkTitleStyles}>
                        {selectedIndex === 2 ? (
                          <MoreIcon label="" size="small" />
                        ) : (
                          <span css={enlargedIconStyles}>
                            <EmojiFrequentIcon label="" size="small" />
                          </span>
                        )}
                        Change History
                      </span>
                    </Link>
                  </span>
                </Tab>
                <Tab>
                  <span css={tabWithLinkStyles}>
                    <Link relative="path" to={`/slo-group/${publicId}/sync`}>
                      <span css={tabWithLinkTitleStyles}>
                        {selectedIndex === 3 ? (
                          <MoreIcon label="" size="small" />
                        ) : (
                          <span css={enlargedIconStyles}>
                            <BitbucketPipelinesIcon label="" size="small" />
                          </span>
                        )}
                        <span css={[flexStart, spaceGapMinimum]}>
                          <span>Sync Operations</span>
                          {hasSyncPlan && (
                            <BreachCount isWarning count={dataSync.operations?.length} />
                          )}
                        </span>
                      </span>
                    </Link>
                  </span>
                </Tab>
              </TabList>
            </span>
            <TabPanel>
              <SLOPanel data={data} />
            </TabPanel>
            <TabPanel>
              <ConfigurationPanel data={data} />
            </TabPanel>
            <TabPanel>
              <RevisionLogs entityType="slogroup" publicId={publicId} />
            </TabPanel>
            <TabPanel>
              <SyncPanel data={dataSync} name={data.name} inProgress={syncInProgress} />
            </TabPanel>
          </Tabs>
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

export default SLOGroupView;
